import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { ButtonTypes } from "@iddataweb/idw-common-ui/enums";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { ORGANIZATION_USERS_URL } from "@constants/routes";
import { OrganizationUserDetailsTabTypes } from "@enums/organization-user-details-tab-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { ModalTypes } from "@enums/modal-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { Auth0IdentityProvider, AxnAdminUserPropertyCategory, IdentityProvider } from "@idw-types/data/reference-data/generic";
import { navigateToUtil } from "@utils/navigation-util";
import { extractRelyingPartiesForPropertyName, isAxnAdminUser } from "@utils/permissions-util";
import {
    getAllowedAxnAdminUserPropertyBundles,
    getAllowedRelyingPartiesForOrganizationId,
    getRolesForUserWithIsCustomFlag
} from "@utils/validation/organization/organization-users/organization-users-util";
import { getOrganizationUserDetailsValidationErrors } from "@utils/validation/organization/organization-user-details/organization-user-details-validation-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { OrganizationUserActivityContainer } from "./organization-user-activity-container/organization-user-activity-container";
import { OrganizationUserDetailsAdminApiAccessTab } from "./organization-user-details-admin-api-access-tab/organization-user-details-admin-api-access-tab";
import { OrganizationUserDetailsMainTab } from "./organization-user-details-main-tab/organization-user-details-main-tab";
import { OrganizationUserDetailsPermissionsPanelTab } from "./organization-user-details-permissions-panel-tab/organization-user-details-permissions-panel-tab";
import styles from "./organization-user-details-component.less";

type OrganizationUserDetailsComponentProps = {
    session: IDWSession;
    axnAdminUser: AxnAdminUser;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    axnIdentityProviders: Array<IdentityProvider>;
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyCategories: Array<AxnAdminUserPropertyCategory>;
    requestInProgress: boolean;
    setRequestInProgress: (requestInProgress: boolean) => void;
    handleSave: (axnAdminUser: AxnAdminUser) => Promise<void>;
};

export const OrganizationUserDetailsComponent = (props: OrganizationUserDetailsComponentProps) => {
    const {
        session,
        requestInProgress,
        axnAdminUser,
        axnIdentityProviders,
        auth0IdentityProviders,
        axnAdminUserPropertyBundles,
        axnRelyingParties,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties,
        axnAdminUserPropertyCategories,
        handleSave,
        setRequestInProgress
    } = props;

    const [currentTab, setCurrentTab] = useState(OrganizationUserDetailsTabTypes.DETAILS);
    const [axnAdminUserState, setAxnAdminUserState] = useState<AxnAdminUser>(cloneDeep(axnAdminUser));
    const [isApiPasswordSet, setIsApiPasswordSet] = useState(axnAdminUser.axnAdminUserDetails.apiUserName !== null);
    const [isApiAccessEnabled, setIsApiAccessEnabled] = useState(axnAdminUser.axnAdminUserDetails.apiUserName !== null);

    useEffect(() => {
        setAxnAdminUserState(axnAdminUser);
    }, [axnAdminUser]);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(axnAdminUser, axnAdminUserState);
    }, [axnAdminUser, axnAdminUserState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-organization-user-details-changes",
        modalType: ModalTypes.DiscardOrganizationUserDetailsChanges,
        stateIsUnchanged
    });

    const { adminProperty, organizationRole, relyingPartyRoles, isCustomOrgRole } = useMemo(() => {
        return getRolesForUserWithIsCustomFlag(
            axnAdminUserState,
            axnRelyingParties,
            axnAdminUserPropertyBundles,
            axnAdminAvailableUserOrganizationProperties,
            axnAdminAvailableUserRPProperties
        );
    }, [
        axnAdminUserState,
        axnRelyingParties,
        axnAdminUserPropertyBundles,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties
    ]);

    const organizationAxnRelyingParties = useMemo(() => {
        return getAllowedRelyingPartiesForOrganizationId(axnRelyingParties, axnAdminUserState.organizationID)
    }, [axnRelyingParties, axnAdminUserState]);

    const allowedAxnAdminPropertyBundles = useMemo(() => {
        return getAllowedAxnAdminUserPropertyBundles(
            session,
            axnRelyingParties,
            axnAdminUserPropertyBundles
        );
    }, [
        axnAdminUserPropertyBundles,
        session.user,
        axnRelyingParties,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties
    ]);

    const navigate = useNavigate();
    const routerLocation = useLocation();

    const onClickSave = async () => {
        setRequestInProgress(true);
        await handleSave(axnAdminUserState);
        setRequestInProgress(false);
    };

    const onCloseClick = () => {
        navigateToUtil(ORGANIZATION_USERS_URL, navigate, routerLocation.pathname);
    };

    const organizationUserDetailValidationErrors = useMemo(() => {
        if (axnAdminUser) {
            return getOrganizationUserDetailsValidationErrors(
                axnAdminUserState,
                adminProperty,
                organizationRole,
                isCustomOrgRole,
                relyingPartyRoles,
                axnAdminUser.axnAdminUserDetails.apiUserName,
                isApiAccessEnabled,
                isApiPasswordSet
            );
        }

        return null;
    }, [
        axnAdminUserState,
        adminProperty,
        organizationRole,
        isCustomOrgRole,
        relyingPartyRoles,
        axnAdminUser,
        isApiPasswordSet,
        isApiAccessEnabled,
    ]);

    const canModifyUser = useMemo(() => {
        if (session.user.axnAdminUserRPProperties.length !== 0) {
            const allowedRelyingParties = extractRelyingPartiesForPropertyName(
                UserPermissionTypes.MODIFY_USERS,
                session.user.axnAdminUserRPProperties,
                axnRelyingParties
            ).map(rp => rp.id);

            return isAxnAdminUser(session.user) || allowedRelyingParties.length > 0;
        } else {
            const canModifyOrgUser = session.user.axnAdminUserOrganizationProperties.some(
                property => property.propertyName === UserPermissionTypes.MODIFY_USERS &&
                    isStringTrue(property.propertyValue)
            );

            return isAxnAdminUser(session.user) || canModifyOrgUser;
        }
    }, [
        session.user,
        axnRelyingParties
    ]);

    return (
        <div className={styles.organizationUserDetailsComponent}>
            <div className={styles.headerContainer}>
                <span className={styles.userName}>
                    {`${axnAdminUserState.axnAdminUserDetails.firstName} ${axnAdminUserState.axnAdminUserDetails.lastName}`}
                </span>
                <div className={styles.buttonContainer}>
                    <Button
                        id="organization-user-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>

                    {currentTab !== OrganizationUserDetailsTabTypes.USER_ACTIVITY && <div>
                        <ButtonWithSpinner
                            id="organization-user-details-submit-button"
                            disabled={requestInProgress || stateIsUnchanged || organizationUserDetailValidationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || organizationUserDetailValidationErrors.count > 0}
                            variant="primary"
                            type={ButtonTypes.BUTTON}
                            onClick={onClickSave}
                            label={resources.common.buttons.save.label}
                        />
                    </div>}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="organization-user-details" fill={true} unmountOnExit={true} onSelect={setCurrentTab}>
                    <Tab eventKey={OrganizationUserDetailsTabTypes.DETAILS} title={resources.pages.organizationUserDetails.tabs.details.tabName}>
                        <OrganizationUserDetailsMainTab
                            isDisabled={requestInProgress || !canModifyUser}
                            canModifyUser={canModifyUser}
                            session={session}
                            isCustomOrgRole={isCustomOrgRole}
                            axnAdminUserState={axnAdminUserState}
                            adminProperty={adminProperty}
                            organizationRole={organizationRole}
                            relyingPartyRoles={relyingPartyRoles}
                            axnAdminAvailableUserOrganizationProperties={axnAdminAvailableUserOrganizationProperties}
                            axnAdminAvailableUserRPProperties={axnAdminAvailableUserRPProperties}
                            axnAdminUserPropertyBundles={allowedAxnAdminPropertyBundles}
                            axnRelyingParties={organizationAxnRelyingParties}
                            axnIdentityProviders={axnIdentityProviders}
                            auth0IdentityProviders={auth0IdentityProviders}
                            validationErrors={organizationUserDetailValidationErrors.details}
                            setAxnAdminUserState={setAxnAdminUserState}
                        />
                    </Tab>
                    {axnAdminUser.permissions.canViewAdminAPIAccess && (
                        <Tab eventKey={OrganizationUserDetailsTabTypes.ADMIN_API_ACCESS} title={resources.pages.organizationUserDetails.tabs.adminApiAccess.tabName}>
                            <OrganizationUserDetailsAdminApiAccessTab
                                isDisabled={requestInProgress || !axnAdminUser.permissions.canModifyAdminAPIAccess}
                                initialAxnAdminUser={axnAdminUser}
                                axnAdminUserState={axnAdminUserState}
                                isApiPasswordSet={isApiPasswordSet}
                                isApiAccessEnabled={isApiAccessEnabled}
                                validationErrors={organizationUserDetailValidationErrors.adminApiAccess}
                                setIsApiAccessEnabled={setIsApiAccessEnabled}
                                setIsApiPasswordSet={setIsApiPasswordSet}
                                setAxnAdminUserState={setAxnAdminUserState}
                            />
                        </Tab>

                    )}
                    {axnAdminUser.permissions.canViewPermissions && (
                        <Tab eventKey={OrganizationUserDetailsTabTypes.PERMISSIONS_PANEL} title={resources.pages.organizationUserDetails.tabs.permissionsPanel.tabName}>
                            <OrganizationUserDetailsPermissionsPanelTab
                                isDisabled={requestInProgress || !axnAdminUser.permissions.canModifyPermissions}
                                session={session}
                                axnAdminUserState={axnAdminUserState}
                                adminProperty={adminProperty}
                                isCustomOrgRole={isCustomOrgRole}
                                organizationRole={organizationRole}
                                relyingPartyRoles={relyingPartyRoles}
                                axnAdminAvailableUserOrganizationProperties={axnAdminAvailableUserOrganizationProperties}
                                axnAdminAvailableUserRPProperties={axnAdminAvailableUserRPProperties}
                                axnRelyingParties={organizationAxnRelyingParties}
                                axnAdminUserPropertyCategories={axnAdminUserPropertyCategories}
                                setAxnAdminUserState={setAxnAdminUserState}
                            />
                        </Tab>
                    )}
                    {axnAdminUser.permissions.canListUserActivity && (
                        <Tab eventKey={OrganizationUserDetailsTabTypes.USER_ACTIVITY} title={resources.pages.organizationUserDetails.tabs.activity.tabName}>
                            <OrganizationUserActivityContainer axnAdminUser={axnAdminUser} />
                        </Tab>
                    )}
                </Tabs>
            </TabContainer>
        </div>
    );
};